/**
 * Innstillinger → Varsler (issues #124 and #133): which push notifications the company sends, how
 * long before a shift the reminder comes, and when the managers' daily «to control» push comes.
 * Saved through PUT /tenant/:id as push_settings (backend services/pushKinds.js checks it). A kind
 * switched off here is off for everybody in the company; each person also has their own switches
 * on the phone under Profil → Varsler.
 */
import type { PushSettings, TenantWrite } from '@/api/company';
import { T } from '@/i18n';
import { Panel, Switch } from '@/ui';

export const DEFAULTS: PushSettings = { off: [], shift_lead_minutes: 60, daily_at: '09:00' };

/** The kinds in the backend's order, with what each one is, in the company's words. */
export const KINDS: { key: string; label: string; about: string; admin: boolean }[] = [
  {
    key: 'shift_reminder',
    label: 'Før vakt',
    about: 'Påminnelse før vakten, og ved start hvis den ansatte ikke har stemplet inn.',
    admin: false,
  },
  {
    key: 'lunch_reminder',
    label: 'Før pause',
    about: 'Fem minutter før en planlagt pause, når den ansatte er stemplet inn.',
    admin: false,
  },
  {
    key: 'plan_published',
    label: 'Vaktplan publisert',
    about: '«Vaktplanen for uke 42 er publisert», én per publisering.',
    admin: false,
  },
  {
    key: 'shift_changed',
    label: 'Vakt endret',
    about: 'En publisert vakt lagt til, flyttet eller fjernet.',
    admin: false,
  },
  {
    key: 'request_answered',
    label: 'Svar på forespørsler',
    about: 'Fravær, bytter, vakter og tilgjengelighet godkjent eller avslått.',
    admin: false,
  },
  {
    key: 'clock_out',
    label: 'Glemt å stemple ut',
    about: '15 minutter etter planlagt slutt, når den ansatte fortsatt er stemplet inn.',
    admin: false,
  },
  {
    key: 'manager_message',
    label: 'Melding fra leder',
    about: 'En melding en leder skriver fra vaktplanen. Den ligger også i varslene i appen.',
    admin: false,
  },
  {
    key: 'requests_waiting',
    label: 'Forespørsler som venter',
    about:
      'Til den som godkjenner: fravær, bytter, vakter og tilgjengelighet. Høyst ett varsel i timen per type, med antallet.',
    admin: true,
  },
  {
    key: 'punch_deviation',
    label: 'Stempling utenfor toleransen',
    about: 'Inn- eller utstempling mer enn 15 minutter fra planen, høyst ett varsel i timen.',
    admin: true,
  },
  {
    key: 'no_clock_in',
    label: 'Ikke stemplet inn',
    about: '15 minutter etter at en vakt startet uten innstempling, én gang per vakt.',
    admin: true,
  },
  {
    key: 'daily_control',
    label: 'Daglig kontroll',
    about: '«3 ting å kontrollere i dag» på tidspunktet under. Ingenting å kontrollere, ingen varsel.',
    admin: true,
  },
  {
    key: 'month_ready',
    label: 'Måned klar',
    about: 'Timer ikke godkjent før lønn, og forrige måned klar til å lukkes.',
    admin: true,
  },
];

export function settingsOf(raw: Partial<PushSettings> | null | undefined): PushSettings {
  return {
    off: Array.isArray(raw?.off) ? raw.off : DEFAULTS.off,
    shift_lead_minutes: Number.isInteger(raw?.shift_lead_minutes)
      ? (raw?.shift_lead_minutes as number)
      : DEFAULTS.shift_lead_minutes,
    daily_at: typeof raw?.daily_at === 'string' ? raw.daily_at : DEFAULTS.daily_at,
  };
}

export default function Varsler({
  value,
  save,
}: { value: Partial<PushSettings> | null | undefined; save: (body: TenantWrite) => void }) {
  const cur = settingsOf(value);
  const write = (next: Partial<PushSettings>) => save({ push_settings: { ...cur, ...next } });
  const toggle = (key: string, on: boolean) =>
    write({ off: on ? cur.off.filter((k) => k !== key) : [...cur.off, key] });
  const group = (admin: boolean) =>
    KINDS.filter((k) => k.admin === admin).map((k) => (
      <div className="set" key={k.key}>
        <div>{T(k.label)}</div>
        <div className="val">
          <Switch
            checked={!cur.off.includes(k.key)}
            onChange={(on) => toggle(k.key, on)}
            label={!cur.off.includes(k.key) ? T('Sendes') : T('Sendes ikke')}
            // the event and who gets it, so eleven switches are not eleven «Sendes» (#267 SET-26)
            name={`${T(k.label)}, ${admin ? T('til lederne') : T('til de ansatte')}`}
          />
          <em>{T(k.about)}</em>
        </div>
      </div>
    ));
  return (
    <>
      <Panel title={T('Til de ansatte')} sub={T('det de ansatte får på telefonen')}>
        {group(false)}
        <div className="set">
          <div>{T('Påminnelse før vakt')}</div>
          <div className="val">
            <input
              className="inp"
              type="number"
              min={5}
              max={240}
              step={5}
              style={{ width: 90 }}
              aria-label={T('Minutter før vakten')}
              key={`lead-${cur.shift_lead_minutes}`}
              defaultValue={cur.shift_lead_minutes}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (Number.isInteger(v) && v >= 5 && v <= 240 && v !== cur.shift_lead_minutes)
                  write({ shift_lead_minutes: v });
              }}
            />{' '}
            {T('minutter før start')}
            <em>{T('Mellom 5 og 240 minutter.')}</em>
          </div>
        </div>
      </Panel>
      <Panel title={T('Til lederne')} sub={T('hver lenke åpner saken det gjelder')}>
        {group(true)}
        <div className="set">
          <div>{T('Tidspunkt for daglig kontroll')}</div>
          <div className="val">
            <input
              className="inp"
              type="time"
              aria-label={T('Tidspunkt for daglig kontroll')}
              key={`at-${cur.daily_at}`}
              defaultValue={cur.daily_at}
              onBlur={(e) => {
                if (/^\d{2}:\d{2}$/.test(e.target.value) && e.target.value !== cur.daily_at)
                  write({ daily_at: e.target.value });
              }}
            />
          </div>
        </div>
      </Panel>
      <p className="hint" style={{ maxWidth: '66ch' }}>
        {T(
          'Hver person kan også slå av sine egne varsler i appen, under Profil → Varsler. Mellom 22:00 og 07:00 venter varslene til morgenen, unntatt påminnelser om personens egen vakt.',
        )}
      </p>
    </>
  );
}
